import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'posta',
  titolo: 'Posta, caselle di gruppo e di progetto',
  gruppo: 'Comunicazione',
  permesso: ['posta.usa', 'impostazione.modifica'],
  aCheServe: 'La voce «Posta» apre la webmail @cidisonlus.org dentro il gestionale: la tua casella, le caselle di '
    + 'gruppo che usi, come qualita@ o privacy@, e quelle dei progetti a cui accedi. Le caselle ospitate stanno nel '
    + 'gestionale: niente server e niente password, la posta arriva da sola.',
  parti: [
    {
      titolo: 'La casella ospitata',
      permesso: 'posta.usa',
      testo: [
        'Una casella **ospitata** sta nel gestionale: la posta arriva direttamente qui e parte dal servizio di invio '
          + 'di CIDIS. Non c’è niente da configurare e nessuna password: nella posta entri con l’accesso al gestionale.',
        'In «Casella personale» puoi cambiare solo il nome che vede chi riceve. «Prova l’invio» ti manda un messaggio '
          + 'di prova. La casella la crea chi gestisce gli utenti, e non si scollega: è l’archivio della tua posta.',
        'Sotto le cartelle leggi «Ricezione: ultimo messaggio oggi alle …». Quando arriva posta nuova ricevi una '
          + 'notifica, al massimo una ogni quarto d’ora per casella.',
        'Finché la posta di CIDIS non passa al gestionale, sotto le cartelle leggi «La ricezione nel gestionale non è '
          + 'ancora attiva»: la casella riceverà da quel giorno.',
      ],
    },
    {
      titolo: 'Collegare la tua casella (su server)',
      permesso: 'posta.usa',
      passi: [
        'Apri «Posta», linguetta «Casella personale». Di solito server e porte sono già scritti.',
        'Nel riquadro «Invio (SMTP)» controlla il nome che vedrà chi riceve e scrivi la «Password» della casella.',
        'Spunta «Usa questa casella» e premi «Salva».',
        'Premi «Prova l’invio»: se tutto va bene leggi «Messaggio di prova inviato a …».',
      ],
      nota: 'La password si scrive ma non si rilegge: resta cifrata.',
    },
    {
      titolo: 'Leggere e rispondere',
      permesso: 'posta.usa',
      passi: [
        'Linguetta «Posta». In alto il **selettore delle caselle** mostra la casella aperta e quanti messaggi hai da '
          + 'leggere nelle altre. Toccalo: accanto a ogni indirizzo vedi il nome, le etichette (gruppo, progetto, ente, '
          + 'sola lettura) e i messaggi da leggere. Scegli la casella da aprire.',
        'All’ingresso si apre la tua casella personale, o l’ultima che hai scelto. «Tutte le caselle», nel selettore e '
          + 'a sinistra, mostra in una lista sola la tua casella e quelle di gruppo; ogni conversazione porta '
          + 'l’indirizzo che l’ha ricevuta.',
        'A sinistra le stesse caselle, con i messaggi da leggere e le cartelle della casella aperta.',
        'Ogni cartella mostra i suoi messaggi, uno per riga, come in Roundcube: «Posta in arrivo» quelli ricevuti, '
          + '«Inviata» quelli partiti, «Bozze» le bozze e i messaggi non partiti. Tocca un messaggio per aprire la sua '
          + 'conversazione su quel messaggio. Se preferisci le conversazioni, le scegli in «Elenco e lettura».',
        'Un messaggio da leggere ha il pallino e mittente e oggetto in grassetto; uno già letto né l’uno né l’altro. '
          + 'Sulle caselle di gruppo la lettura vale per tutti: «aperto da» dice chi l’ha aperto.',
        'Nella conversazione aperta il messaggio più recente sta in alto, aperto; i più vecchi sotto, chiusi: toccane '
          + 'uno per aprirlo. L’ordine si cambia in «Elenco e lettura».',
        'Aperto un messaggio, leggi l’indirizzo completo di chi scrive e dei destinatari, per esempio «Anna Rossi '
          + '<anna.rossi@ente.it>», nelle righe «Da», «A» e «Cc». Gli indirizzi si selezionano; «Copia», accanto a '
          + 'ciascuno, copia l’indirizzo e diventa «Copiato». A messaggio chiuso passa sopra il nome per vederlo.',
        'Subito sotto «Da», «A» e «Cc» ci sono gli **allegati**, uno per riga: nome, peso e «Scarica». La graffetta '
          + 'con il numero, accanto alla data del messaggio, porta agli allegati e apre il messaggio se è chiuso. Un '
          + 'allegato non ancora controllato dall’antivirus si scarica dopo un avviso; uno infetto non ha «Scarica».',
        'Sotto gli allegati: «Stampa» apre la finestra di stampa con un foglio pulito (oggetto, indirizzi, data, '
          + 'elenco degli allegati e testo); «Salva il messaggio in PDF» è lo stesso foglio, salvato con «Salva come '
          + 'PDF» nella finestra di stampa: gli allegati non ci entrano, si scaricano con «Scarica»; «Scarica .eml» '
          + 'scarica il messaggio come file di posta, ricomposto dal gestionale.',
        '«Solo non lette», sopra l’elenco, mostra solo le conversazioni da leggere, anche in «Tutte le caselle» e '
          + 'con la ricerca. Premilo di nuovo per vedere tutto.',
        'La risposta parte sempre dalla casella che ha ricevuto il messaggio. In un messaggio nuovo scegli la '
          + 'casella nel campo «Da»: ci sono solo le caselle che spediscono, non quelle spente.',
        'Nel testo c’è già la tua firma: nome, sede, telefoni, email e gli avvisi di riservatezza. La puoi '
          + 'modificare nel messaggio; i telefoni li scrivi in «Il mio profilo».',
        'Mentre la pagina è aperta la posta si aggiorna da sola: le caselle che guardi si rileggono ogni uno o due '
          + 'minuti, l’elenco ogni trenta secondi. Sotto la casella leggi «aggiornata alle 10:42»; durante la lettura '
          + 'gira una piccola rotella.',
        'Nelle caselle su server, «Aggiorna ora», sotto le cartelle, legge subito i messaggi nuovi. Le caselle '
          + 'ospitate non ne hanno bisogno: la posta arriva da sola.',
      ],
      nota: 'In fondo ai messaggi che scrivi il gestionale aggiunge l’avviso sull’archiviazione della posta; non lo '
        + 'aggiunge alle email automatiche, come inviti e codici di accesso. Il .eml non è il file originale: il '
        + 'gestionale conserva le parti del messaggio e con queste lo ricompone nel file; gli allegati non '
        + 'controllati dall’antivirus restano fuori. Ogni .eml scaricato resta nel registro eventi.',
      immagine: {
        file: 'posta.webp', larghezza: 1200, altezza: 800,
        descrizione: 'La webmail con Tutte le caselle a sinistra, l’elenco delle conversazioni e un messaggio aperto.',
      },
    },
    {
      titolo: 'Una casella spenta',
      permesso: 'posta.usa',
      testo: [
        'Una casella **spenta** non riceve e non spedisce più: capita alla vecchia casella personale quando la posta '
          + 'passa a un’altra casella, per esempio a una casella di gruppo. A sinistra porta l’etichetta «spenta» e '
          + 'sotto le cartelle leggi «Casella spenta: non spedisce».',
        'La puoi consultare: apri le conversazioni e i messaggi, scarichi gli allegati e il .eml. Non rispondi, non '
          + 'archivi, non sposti nel cestino e non segni letto o da leggere. Nel campo «Da» non compare: «Scrivi», da '
          + 'qui, scrive dalla tua casella accesa.',
        'Una bozza che avevi scritto da quella casella resta tua, nella sua cartella «Bozze»: aprila e scegli '
          + 'un’altra casella in «Da». Una risposta invece parte solo dalla casella che ha ricevuto la conversazione: '
          + 'copia il testo in un messaggio nuovo.',
      ],
      nota: 'Lo stesso per una casella collegata a un server che non ha il server di invio: si legge, ma nel «Da» '
        + 'non c’è, e sotto le cartelle leggi perché.',
    },
    {
      titolo: 'Se un messaggio non parte',
      permesso: 'posta.usa',
      testo: [
        'Se il server di posta non accetta il messaggio, la finestra per scrivere resta aperta con «**Non inviato**» e '
          + 'il motivo, per esempio «il server di posta non risponde». Il messaggio non è partito e non è fra gli '
          + 'inviati: sta nella cartella «Bozze» della casella, con «Non inviato» in rosso.',
        'Resta un messaggio solo, anche se riprovi più volte: ogni tentativo rifà lo stesso messaggio, non ne crea uno '
          + 'nuovo. Quando parte passa in «Inviata».',
      ],
      passi: [
        'Nella finestra per scrivere: correggi se serve e premi di nuovo «Invia».',
        'Più tardi: apri «Bozze», tocca il messaggio con «Non inviato». Sopra il messaggio leggi il motivo.',
        '«Riprova» lo rispedisce com’è. «Modifica» lo riapre nella finestra per scrivere, dove puoi cambiare '
          + 'destinatari, testo o la casella in «Da», e poi premere «Invia».',
        'Se non serve più, aprilo con «Modifica» e premi «Elimina la bozza»: se ne va anche il «Non inviato».',
      ],
      nota: 'Un messaggio a scadenza si rifà con «Modifica»: la password non si conserva, e la scegli di nuovo.',
    },
    {
      titolo: 'Più spazio per leggere',
      permesso: 'posta.usa',
      passi: [
        'Sul computer la posta occupa tutta la finestra: caselle, elenco e conversazione arrivano in fondo allo '
          + 'schermo e scorrono ciascuno per conto suo. Il testo dei messaggi si legge intero, senza riquadri da '
          + 'scorrere. Su uno schermo grande le colonne si allargano: caselle ed elenco fino a un limite, la '
          + 'conversazione prende il resto.',
        '«Apri in una nuova schermata», in alto a destra accanto al titolo «Posta», apre tutta la posta in una '
          + 'finestra del browser a parte, grande quanto lo schermo, senza il menu e la testata del gestionale. '
          + 'Premuto di nuovo riporta davanti la finestra già aperta, senza perdere quello che stavi scrivendo. Nella '
          + 'finestra «Torna al gestionale» la chiude e ti riporta al gestionale. Sul telefono e nell’app CIDIS il '
          + 'comando non c’è.',
        'Il comando con il rettangolo diviso, in cima all’elenco, nasconde la colonna di caselle e cartelle; la '
          + 'cartella, o il cestino, si sceglie allora dal menu sotto la ricerca. Il gestionale si ricorda la scelta su '
          + 'quel computer.',
        'Nella conversazione aperta, in alto a destra: le due frecce verso l’esterno la mostrano a tutta pagina, '
          + 'senza menu né elenco; le due frecce verso l’interno tornano alla posta.',
        'Il quadrato con la freccia apre la conversazione in una nuova scheda del browser, a tutta pagina: si '
          + 'tiene aperta accanto alla posta.',
        'La finestra per scrivere è larga quanto la pagina e il testo ha l’altezza di mezzo schermo.',
      ],
    },
    {
      titolo: 'La rubrica',
      permesso: 'posta.usa',
      testo: [
        'Ogni casella ha la sua **rubrica**: gli indirizzi a cui scrivi da quella casella, e quante volte. Si riempie '
          + 'da sola a ogni invio, e all’avvio ha preso gli indirizzi della posta già inviata. I colleghi di CIDIS ci '
          + 'sono sempre: non serve aggiungerli.',
      ],
      passi: [
        'Mentre scrivi in «A», «Copia (cc)» o «Copia nascosta (ccn)» compaiono i suggerimenti: prima gli indirizzi a '
          + 'cui scrivi più spesso, poi i colleghi. Scegli con le frecce e Invio, oppure tocca la voce; Esc li '
          + 'chiude.',
        'Linguetta «Rubrica»: scegli la casella, cerca per nome o indirizzo e premi «Scrivi» per un messaggio nuovo a '
          + 'quel contatto.',
        '«Correggi il nome» cambia come il contatto compare nei suggerimenti. «Togli» lo toglie dalla rubrica: se gli '
          + 'scrivi di nuovo da quella casella, torna.',
        '«Aggiungi un contatto» lo mette in rubrica senza aspettare un invio.',
      ],
      nota: 'Sulle caselle di gruppo e di progetto la rubrica è della casella: la usa e la corregge chi scrive dalla '
        + 'casella. Con la sola lettura la consulti soltanto. Aggiunte, correzioni e voci tolte restano nel registro '
        + 'eventi.',
    },
    {
      titolo: 'I gruppi della rubrica',
      permesso: 'posta.usa',
      testo: [
        'Un **gruppo** dà un nome a più contatti, per esempio «Comune di Asti» per gli uffici del comune. Se scrivi '
          + 'il nome del gruppo in «A», «Copia (cc)» o «Copia nascosta (ccn)», arrivano tutti i suoi indirizzi.',
      ],
      passi: [
        'Linguetta «Rubrica», riquadro «Gruppi»: premi «Nuovo gruppo», scrivi il nome e spunta i contatti. Puoi '
          + 'spuntare anche i colleghi: entrano nella rubrica della casella. Premi «Salva».',
        '«Modifica» cambia il nome o i contatti; «Elimina» toglie il gruppo, i contatti restano. «Scrivi» apre un '
          + 'messaggio nuovo a tutto il gruppo.',
        'Nel messaggio, mentre scrivi in «A», il gruppo compare fra i suggerimenti con quanti indirizzi ha: sceglilo '
          + 'e arrivano gli indirizzi. Se scrivi il nome intero e passi a un altro campo, il gestionale lo '
          + 'sostituisce da solo con gli indirizzi, e lo fa comunque prima dell’invio.',
      ],
      nota: 'Il nome del gruppo non può avere @, virgole o punti e virgola, e nella stessa casella non si ripete. Un '
        + 'contatto tolto dalla rubrica esce anche dai gruppi. I gruppi li crea e li cambia chi scrive dalla casella; la '
        + 'rubrica della casella dell’ente si consulta soltanto.',
    },
    {
      titolo: 'Importare ed esportare la rubrica',
      permesso: 'posta.usa',
      passi: [
        'Linguetta «Rubrica», scegli la casella e premi «Importa da file». Scegli un file vCard (.vcf) o CSV, come lo '
          + 'esportano Gmail, Outlook (anche in italiano), Thunderbird o il telefono.',
        'Il gestionale mostra l’anteprima: per ogni indirizzo «nuovo», «già in rubrica», «doppione nel file» o '
          + '«indirizzo non valido». Entrano solo i nuovi; i contatti già in rubrica restano come sono, e prendono il '
          + 'nome del file solo se non ne avevano uno.',
        'Se il file ha dei gruppi (le categorie di Outlook, le etichette di Gmail), «Metti i contatti nei gruppi del '
          + 'file» li crea e ci mette i contatti. Premi «Importa».',
        '«Esporta in vCard» ed «Esporta in CSV» scaricano la rubrica della casella, con i gruppi. La vCard la leggono '
          + 'Outlook, Gmail, Thunderbird e i telefoni; il CSV si apre anche con Excel.',
      ],
      nota: 'Il file da importare si legge sul tuo computer: al gestionale arrivano solo nomi, indirizzi e gruppi. Al '
        + 'massimo 5.000 indirizzi per volta. Esportare porta fuori dal gestionale dati personali: lo fa solo chi '
        + 'scrive dalla casella (non dalla casella dell’ente, non con la sola lettura), e resta nel registro eventi con '
        + 'il numero dei contatti. Importazioni e gruppi restano nel registro come le altre modifiche.',
    },
    {
      titolo: 'Cercare nella posta',
      permesso: 'posta.usa',
      passi: [
        'Scrivi nel campo «Cerca» sopra l’elenco: una o più parole, un mittente, un indirizzo. La ricerca parte da '
          + 'sola dopo mezzo secondo.',
        'Con più parole escono le conversazioni con un messaggio che le contiene tutte, in qualsiasi ordine. '
          + 'Maiuscole e accenti non contano: «citta» trova anche «Città».',
        'Accanto al campo scegli dove cercare: «Tutte le cartelle» (la scelta di partenza) guarda tutta la casella '
          + 'tranne Indesiderata e Cestino del server, fra le conversazioni aperte e archiviate; «Questa cartella» solo '
          + 'la cartella aperta; «Tutte le caselle» anche le altre caselle che usi, come «Tutte le cartelle». L’ultima '
          + 'scelta resta per la volta dopo.',
        'Ogni risultato dice in quale cartella sta, con «archiviata» se la conversazione è archiviata, e con «Tutte '
          + 'le caselle» anche in quale casella. Vale sia nell’elenco per conversazioni sia in quello per singoli '
          + 'messaggi.',
        'Le parole intere e gli inizi di parola da 4 lettere in su si cercano in tutto l’archivio, anche nella posta '
          + 'arrivata dal vecchio server: oggetto, mittente, destinatari, testo e nomi degli allegati. «fatt» trova '
          + '«fattura», «parol» trova paroliamo@cidisonlus.org. Le parole di 2 o 3 lettere valgono solo intere.',
        'Se così non trova niente, la ricerca guarda anche dentro le parole («attura» in «fattura»), ma solo nelle '
          + 'conversazioni degli ultimi 90 giorni, su oggetto, anteprima e indirizzi.',
        'Durante la ricerca leggi «Ricerca in corso…». Se in fondo all’elenco compare '
          + '«Cerca nei messaggi più vecchi», la ricerca si è fermata a un certo punto dell’archivio: premilo per '
          + 'continuare.',
      ],
      nota: '«Nessun messaggio trovato finora» vuol dire che resta archivio da guardare; «Nessun messaggio trovato», '
        + 'che la ricerca è arrivata in fondo. La ricerca guarda solo le caselle che puoi aprire. Nel cestino si cerca '
        + 'solo nel cestino. La tendina «aperte / archiviate» vale per la ricerca solo con «Questa cartella»; con le '
        + 'altre scelte, mentre cerchi, resta ferma.',
    },
    {
      titolo: 'Il cestino',
      permesso: 'posta.usa',
      testo: [
        'Le conversazioni che non ti servono più vanno nel **cestino**. Ci restano 30 giorni, poi le cancella la regola '
          + 'di cancellazione della posta, con il suo preavviso. Finché ci sono, le ripristini.',
      ],
      passi: [
        'Apri la conversazione e premi «Sposta nel cestino», accanto ad «Archivia». Sparisce dall’elenco.',
        'A sinistra, sotto le cartelle, la voce «Cestino» dice quante conversazioni contiene. Toccala per vederle: '
          + 'ognuna dice da quando è nel cestino. Con la colonna nascosta il cestino è nel menu delle cartelle sotto la '
          + 'ricerca; in «Tutte le caselle» il cestino raccoglie quello di tutte le tue caselle.',
        'Apri una conversazione del cestino e premi «Ripristina»: torna fra le aperte, nella sua cartella.',
        'La tendina sopra l’elenco sceglie fra conversazioni «aperte» e «archiviate»; nel cestino non serve.',
        'La ricerca vale per l’elenco che guardi: fuori dal cestino lascia fuori le conversazioni cestinate, nel '
          + 'cestino cerca solo fra quelle.',
      ],
      nota: 'Sulle caselle di gruppo e di progetto il cestino è uno per tutti: quello che sposti tu lo vedono nel cestino '
        + 'anche i colleghi, e chiunque scriva dalla casella lo può ripristinare. Con la sola lettura, in supervisione e '
        + 'impersonando un utente, i comandi non ci sono. Se nella conversazione arriva un messaggio nuovo, questa '
        + 'resta nel cestino, come una conversazione archiviata resta archiviata, ma i 30 giorni ripartono dal '
        + 'messaggio. Le conversazioni collegate a una scheda non si cancellano, salvo che la regola le comprenda; se '
        + 'la regola è spenta il cestino non si svuota. «Cestino del server», fra le cartelle, è la cartella cestino '
        + 'che arriva dal server di posta: non è il cestino del gestionale e non si svuota.',
    },
    {
      titolo: 'La posta indesiderata',
      permesso: 'posta.usa',
      testo: [
        'Il filtro antispam di CIDIS mette nella cartella «Indesiderata» la posta che giudica indesiderata. Se sbaglia, '
          + 'correggilo dalla conversazione: impara da quello che segni.',
      ],
      passi: [
        'Apri la conversazione e premi «È indesiderata»: i messaggi ricevuti vanno nella cartella «Indesiderata» e la '
          + 'conversazione esce dall’elenco.',
        'Se nell’«Indesiderata» c’è una conversazione buona, aprila e premi «Non è indesiderata»: torna nella «Posta '
          + 'in arrivo».',
        'Ogni volta il filtro del server impara dai messaggi arrivati da fuori. Le email dei colleghi si spostano, ma '
          + 'il filtro non impara da queste.',
      ],
      nota: 'Il comando c’è sulle caselle di CIDIS, per chi ha l’accesso pieno: non con la sola lettura, in supervisione '
        + 'o impersonando un utente. Sulle caselle di gruppo e di progetto la cartella è una per tutti. Ogni '
        + 'segnalazione resta nel registro degli eventi.',
    },
    {
      titolo: 'Bandierine ed etichette',
      permesso: 'posta.usa',
      testo: [
        'Una **bandierina** colorata segna una conversazione da tenere d’occhio: la riga dell’elenco resta del suo '
          + 'colore, con una barra a sinistra e l’icona della bandierina. I colori sono sei: rossa, arancione, gialla, '
          + 'verde, blu, viola. Il significato lo decidete voi. Una bandierina per conversazione.',
        'Le **etichette** sono parole che attacchi alle conversazioni, per esempio «Pratica Rossi» o «Da richiamare». '
          + 'Una conversazione può averne più d’una.',
        'Bandierine ed etichette possono essere **per tutti** o **solo per te**. Quelle per tutti le vede chi usa la '
          + 'casella, anche di gruppo o di progetto, come lo stato «letta»; quelle solo per te le vedi tu. Una '
          + 'conversazione può avere la bandierina per tutti e la tua insieme: le vedi entrambe, due strisce e due '
          + 'icone. Tenendo il puntatore sull’icona leggi il colore e «per tutti» o «solo per te».',
        'I messaggi segnati nel vecchio Roundcube sono diventati una bandierina rossa per tutti sulla loro conversazione.',
      ],
      passi: [
        'Apri la conversazione e premi «Bandierina ed etichette», accanto agli altri comandi. Nell’elenco lo stesso '
          + 'menu si apre con l’icona della bandierina in fondo a ogni riga. Sul telefono il menu sale dal basso.',
        'In «Per tutti» o in «Solo per te» tocca un colore: la bandierina è messa. Toccalo di nuovo, o premi «Togli», '
          + 'per toglierla.',
        'Sotto «Etichette» spunta quelle da mettere e togli la spunta a quelle da togliere.',
        'Per crearne una scrivi il nome in «Nuova etichetta», scegli «Solo per me» o «Per tutti quelli che usano la '
          + 'casella» e premi «Crea e metti»: la crei e la metti sulla conversazione. Le tue valgono in tutte le caselle '
          + 'che leggi; quelle per tutti appartengono alla casella.',
        'Accanto a ogni etichetta «Rinomina» cambia il nome ed «Elimina» la cancella: sparisce da tutte le '
          + 'conversazioni. Chiede conferma.',
        'Sotto il campo «Cerca», la tendina «Bandierina: tutte» mostra solo le conversazioni con una bandierina («Con '
          + 'bandierina») o con una di un colore; «Etichetta: tutte» solo quelle con l’etichetta scelta. Le due '
          + 'tendine guardano in tutte le cartelle della casella, fra le conversazioni aperte o archiviate secondo la '
          + 'tendina in alto. Valgono con «Solo non lette», con la ricerca e in «Tutte le caselle». Se tocchi una '
          + 'cartella o il cestino, si tolgono.',
      ],
      nota: 'Le bandierine e le etichette per tutti le mette, le toglie e le gestisce chi ha l’accesso pieno alla '
        + 'casella. Con la sola lettura, in supervisione e nella casella dell’ente usi le tue. Se impersoni un '
        + 'utente, il menu non c’è. Le etichette e le bandierine «solo per me» stanno solo nel gestionale. Su una '
        + 'casella usata anche da un programma di posta la bandierina «per tutti» è la stella o la bandierina del '
        + 'programma, nei due sensi; il colore resta del gestionale. Se la '
        + 'conversazione viene cancellata (cestino o regola di cancellazione), bandierine ed etichette se ne vanno '
        + 'con lei. I nomi delle etichette possono contenere dati personali: stanno cifrati, come i messaggi.',
    },
    {
      titolo: 'La risposta di assenza',
      permesso: 'posta.usa',
      testo: [
        'Quando sei via, la **risposta di assenza** risponde da sola a chi scrive alla casella, come la «risposta '
          + 'automatica» di Roundcube. La imposti per ogni casella in cui scrivi: la tua e quelle di gruppo e di progetto '
          + 'con «legge e scrive».',
      ],
      passi: [
        'Apri la casella in «Posta». A sinistra, sotto le cartelle, premi «Assenza e regole»: si apre la linguetta '
          + '«Risposta di assenza».',
        'Scrivi il «Testo», per esempio «Sono fuori ufficio fino al 10 novembre. Per urgenze scrivete a …». L’«Oggetto» '
          + 'è facoltativo: senza, la risposta parte con «Risposta automatica:» e l’oggetto del messaggio ricevuto.',
        'Scegli «Dal giorno» e, se lo sai, «Al giorno»: senza il giorno di fine risponde finché non la spegni.',
        'Sulla tua casella, se hai l’agenda, «Segna anche nell’agenda» ti segna «non disponibile» negli stessi giorni, '
          + 'senza motivo: serve «Al giorno».',
        'Spunta «Risposta di assenza accesa» e premi «Salva». Sotto le cartelle leggi «Risposta di assenza accesa fino '
          + 'al …».',
        'Al rientro togli la spunta e premi «Salva»; dopo il giorno di fine smette da sola.',
      ],
      nota: 'A ogni mittente risponde una volta ogni 7 giorni; spenta e riaccesa, ricomincia. Non risponde a liste e '
        + 'newsletter, a messaggi automatici e ad altre risposte automatiche, a indirizzi come noreply, alla posta '
        + 'indesiderata, ai mittenti con l’etichetta «falso CIDIS?» o «esterno, non verificato», alle altre caselle '
        + 'che usi e a chi ti ha messo in copia nascosta. Le caselle @esterni che scrivono solo dentro CIDIS '
        + 'rispondono solo ai colleghi. La risposta non va in «Inviata»: in «Ultime risposte» vedi a chi è partita e '
        + 'quando, negli ultimi 30 giorni. Accensione, spegnimento e modifiche restano nel registro eventi.',
    },
    {
      titolo: 'Le regole di smistamento',
      permesso: 'posta.usa',
      testo: [
        'Le **regole** smistano i messaggi che arrivano nella «Posta in arrivo», come i filtri di Roundcube: li spostano '
          + 'in una cartella, li segnano come letti, mettono la bandierina o un’etichetta, fermano le regole successive. '
          + 'Non toccano la posta indesiderata.',
      ],
      passi: [
        '«Assenza e regole», linguetta «Regole», poi «Nuova regola».',
        'In «Quando» scegli se il messaggio deve rispettare tutte le condizioni o almeno una.',
        'Ogni condizione ha un campo («Mittente», «Destinatario (A o Cc)», «Oggetto»), un confronto («contiene», «non '
          + 'contiene», «è») e un testo. «Aggiungi una condizione» ne aggiunge un’altra, la × la toglie.',
        'Scegli le azioni: «Sposta nella cartella» con la cartella, «Segna come letta», «Metti la bandierina» con il '
          + 'colore, «Metti l’etichetta» con l’etichetta, «Ferma le regole successive». Premi «Salva la regola»: va in '
          + 'fondo all’elenco.',
        'Le regole valgono dalla prima all’ultima. «Su» e «Giù» cambiano l’ordine, «Accesa» le spegne e le riaccende una '
          + 'per una, «Modifica» ed «Elimina» fanno quello che dicono.',
        '«Applica ora alla Posta in arrivo» passa le regole accese sui messaggi già arrivati e dice quanti ne ha spostati '
          + 'e segnati come letti, e a quante conversazioni ha messo bandierina ed etichette.',
      ],
      nota: 'Maiuscole e minuscole non contano; «è» vuole il testo intero. La bandierina e l’etichetta che una '
        + 'regola mette sulla conversazione sono «per tutti» quelli che usano la casella; le etichette sono quelle '
        + 'per tutti della casella (si creano dal menu «Bandierina ed etichette» di una conversazione) e non portano '
        + 'il nome di nessuno. Se due regole spostano lo stesso messaggio, o mettono la bandierina, vale la prima; '
        + 'le etichette si sommano; le altre azioni delle regole successive valgono, a meno di '
        + '«Ferma le regole successive». L’avviso di posta nuova arriva dopo le regole: non arriva per i messaggi '
        + 'che una regola segna come letti o mette in «Indesiderata», e per quelli spostati porta alla cartella. Se '
        + 'la cartella o l’etichetta di una regola non c’è più, la regola si spegne e lo dice: premi «Modifica» e '
        + 'scegline un’altra. Le condizioni stanno cifrate; creazione, modifica ed eliminazione restano nel '
        + 'registro eventi.',
    },
    {
      titolo: 'Più conversazioni insieme',
      permesso: 'posta.usa',
      passi: [
        'Spunta la casella a sinistra delle righe che ti servono. Per un intervallo spunta la prima, poi tieni premuto '
          + 'Maiusc e spunta l’ultima. «Seleziona tutte», sopra l’elenco, sceglie tutte le righe caricate (anche con '
          + 'Ctrl+A, o ⌘A sul Mac).',
        'Sopra l’elenco compare la barra delle scelte: «Archivia» (o «Riporta fra le aperte»), «Sposta nel cestino» '
          + '(nel cestino «Ripristina»), «Segna come lette», «Segna come da leggere», «Sposta in…», «Bandierina…» ed '
          + '«Etichetta…».',
        'Se qualche conversazione non si può cambiare (sola lettura, una bandierina per tutti senza l’accesso pieno), '
          + 'le altre si cambiano lo stesso, e una riga sotto la barra dice quante sono rimaste com’erano.',
        '«Annulla la selezione», o Esc, toglie le spunte.',
      ],
      nota: 'Nella vista per messaggi le azioni valgono per le conversazioni dei messaggi scelti.',
    },
    {
      titolo: 'Le tue cartelle',
      permesso: 'posta.usa',
      testo: [
        'Nelle caselle del gestionale (ospitate) puoi avere cartelle tue, anche una dentro l’altra, come in Roundcube. '
          + 'Le crea e le gestisce chi ha l’accesso pieno alla casella; le cartelle di sistema (Posta in arrivo, Inviata, '
          + 'Bozze, Archivio, Indesiderata, Cestino del server) non si rinominano e non si eliminano.',
      ],
      passi: [
        'A sinistra, sotto le cartelle, premi «Cartelle…». Scrivi il nome in «Nuova cartella», scegli in «Dove» se va '
          + 'al primo livello o dentro un’altra tua cartella, e premi «Crea la cartella».',
        'Accanto a ogni cartella tua: «Rinomina» ed «Elimina». Se elimini una cartella, le conversazioni che '
          + 'conteneva tornano nella Posta in arrivo: niente si cancella, e la pagina chiede conferma con il loro '
          + 'numero. Le regole che vi spostavano la posta si spengono.',
        'Per spostare una conversazione: aperta, «Sposta in…»; nell’elenco, spunta le righe e usa «Sposta in…» della '
          + 'barra, oppure trascina le righe sulla cartella a sinistra. La posta che hai mandato resta in «Inviata».',
        '«Segna come letta» nella conversazione aperta; «Segna tutte come lette», sopra l’elenco, segna letti tutti i '
          + 'messaggi della cartella.',
        'Se una regola ha spostato un messaggio arrivato, l’avviso «posta nuova» apre la sua cartella.',
      ],
      nota: 'Su una casella usata anche da un programma di posta («Configura su client di posta») cartelle e '
        + 'spostamenti valgono nei due sensi: una cartella creata, rinominata o tolta qui lo è anche nel programma, e '
        + 'viceversa. Sulle caselle che il gestionale legge da un altro server (IMAP) le cartelle sono '
        + 'quelle del server: le tue cartelle e «Sposta in…» arrivano quando la casella passa al gestionale. I nomi '
        + 'che scrivi creando o rinominando una cartella possono dire di chi si tratta: stanno cifrati, come le '
        + 'etichette.',
    },
    {
      titolo: 'Ordine, vista e lettura',
      permesso: 'posta.usa',
      passi: [
        'Sopra l’elenco premi l’ingranaggio, «Elenco e lettura».',
        '«Ordina per»: data, mittente, oggetto o dimensione, in un verso o nell’altro. Per mittente e oggetto si '
          + 'ordinano le 2000 conversazioni più recenti; con la ricerca l’elenco resta in ordine di data.',
        '«Vista»: per messaggi, una riga per messaggio, come in Roundcube: ogni cartella mostra solo i suoi messaggi. È '
          + 'quella di partenza. Oppure per conversazioni: i messaggi di una conversazione in una riga, anche quelli di '
          + 'altre cartelle (in «Posta in arrivo» anche le tue risposte). Se tocchi un messaggio, si apre la sua '
          + 'conversazione, su quel messaggio.',
        '«Righe per pagina»: 20, 30, 50 o 100.',
        '«Un messaggio aperto diventa letto»: appena lo apri, dopo i secondi che scegli, o mai da solo (con «Segna '
          + 'come letta»).',
        '«Nella conversazione aperta»: il messaggio più recente in alto (la scelta di partenza) o in fondo, '
          + 'com’era fino alla versione 1.35.2. Vale anche a tutta pagina e nella nuova scheda del browser.',
        '«Rispondendo, scrivo»: sopra o sotto il messaggio citato. Premi «Salva».',
      ],
      nota: 'Le preferenze stanno sul server: valgono per te su ogni computer e telefono e su tutte le caselle che '
        + 'leggi. Se avevi già scelto e salvato le conversazioni, restano le conversazioni. Con un ordine diverso dalla '
        + 'data, o per messaggi, l’elenco non si aggiorna da solo: premi «Aggiorna».',
    },
    {
      titolo: 'Scorciatoie da tastiera',
      permesso: 'posta.usa',
      passi: [
        'Frecce ↓ e ↑: riga successiva e precedente; Invio apre la conversazione.',
        'Esc chiude la conversazione aperta; premuto di nuovo toglie la selezione.',
        'Canc (⌫ sul Mac) sposta nel cestino le conversazioni scelte, o quella aperta.',
        'R rispondi, A rispondi a tutti, F inoltra.',
        'Ctrl+A (⌘A sul Mac) sceglie tutte le righe; «?» mostra l’elenco delle scorciatoie.',
      ],
      nota: 'Le scorciatoie non valgono mentre scrivi in un campo o con una finestra aperta. Se impersoni un utente, '
        + 'Canc, R, A e F non fanno niente.',
    },
    {
      titolo: 'Grassetto, elenchi e collegamenti',
      permesso: 'posta.usa',
      testo: [
        'Sopra il «Messaggio» c’è la barra della formattazione: «G» grassetto, «C» corsivo, «S» sottolineato, elenco '
          + 'puntato, elenco numerato, citazione, togli formattazione e collegamento. Seleziona il testo e premi il '
          + 'comando; premuto di nuovo, lo toglie. Il nome di ogni comando compare se ci tieni sopra il puntatore.',
      ],
      passi: [
        'Da tastiera: Ctrl+B grassetto, Ctrl+I corsivo, Ctrl+U sottolineato, Ctrl+K collegamento (sul Mac Cmd al '
          + 'posto di Ctrl). Con Tab passi dalla barra al testo.',
        'Collegamento: seleziona le parole, premi il comando e scrivi l’indirizzo, che deve iniziare con https:// '
          + 'oppure essere un indirizzo email. Sotto il campo leggi «Porta a:» con l’indirizzo completo; premi '
          + '«Inserisci». Senza testo selezionato il collegamento mostra l’indirizzo stesso.',
        'Testo incollato da Word, Outlook o da un sito: restano paragrafi, elenchi, grassetti e collegamenti; '
          + 'spariscono caratteri, colori, immagini e tabelle.',
        'Chi riceve legge il messaggio formattato; i programmi di posta che mostrano solo il testo semplice '
          + 'ricevono la versione senza formattazione, con l’indirizzo dei collegamenti fra < >.',
      ],
      nota: 'Le immagini nel testo non si inseriscono: si mandano come allegato.',
    },
    {
      titolo: 'Conferma di lettura e priorità',
      permesso: 'posta.usa',
      passi: [
        'Nel messaggio che scrivi, sotto gli allegati: «Chiedi la conferma di lettura» chiede a chi riceve di '
          + 'confermare che l’ha aperto; «Priorità alta» fa arrivare il messaggio segnato come importante.',
        'Quando una conferma arriva, la trovi in «Posta in arrivo» come un messaggio («Letto: …»), e nel messaggio '
          + 'che hai spedito leggi «Letto da … il …». Finché non arriva leggi «Conferma di lettura chiesta: ancora '
          + 'nessuna risposta».',
        'Se chi ti scrive chiede la conferma, aprendo il messaggio leggi «Il mittente chiede una conferma di '
          + 'lettura» con «Invia la conferma» e «Ignora». La conferma non parte se non la mandi tu.',
        'I messaggi con la priorità alta hanno un «!» rosso accanto all’oggetto, nell’elenco, e accanto al mittente '
          + 'nella conversazione.',
      ],
      nota: 'La conferma dice che il messaggio è stato aperto, non che è stato letto: chi riceve può sempre non '
        + 'mandarla. La scelta («Invia la conferma» o «Ignora») vale per il messaggio, anche per i colleghi di una '
        + 'casella di gruppo, e l’avviso non torna. Non si propone nei messaggi in «Indesiderata» o segnati '
        + '«falso CIDIS?», con la sola lettura, in supervisione e impersonando un utente. Se la conferma non parte, '
        + 'il motivo è scritto sotto i comandi e l’avviso resta. I messaggi segnati come importanti nella vecchia '
        + 'casella non hanno il «!».',
    },
    {
      titolo: 'Inoltrare, con gli allegati o come allegato, e vedere l’originale',
      permesso: ['posta.usa', 'utente.gestisci', 'posta.archivio'],
      passi: [
        '«Inoltra» apre un messaggio nuovo con il testo dell’ultimo messaggio e i suoi **allegati**. Quelli non ancora '
          + 'controllati dall’antivirus, e i programmi, restano fuori, e il messaggio nuovo li elenca in cima. Un '
          + 'allegato che non vuoi mandare lo togli con la × accanto al suo nome.',
        'Ogni messaggio della conversazione ha i suoi «Inoltra» e «Inoltra come allegato», sotto gli indirizzi: '
          + 'inoltrano quel messaggio, con la sua citazione e i suoi allegati. Servono quando gli allegati stanno in '
          + 'un messaggio prima delle risposte.',
        'Nella conversazione aperta, accanto a «Inoltra», «Inoltra come allegato» apre un messaggio nuovo con '
          + 'l’ultimo messaggio allegato come file .eml: chi lo riceve lo apre con il suo programma di posta, con '
          + 'mittente, data, testo e allegati.',
        'Sotto gli indirizzi di ogni messaggio, «Mostra originale» apre una finestra con le **intestazioni** del '
          + 'messaggio (il percorso fra i server, i controlli di DKIM, SPF e DMARC, la priorità) e il **sorgente**. '
          + '«Copia» le copia, per esempio per mandarle a chi controlla un messaggio sospetto.',
        'Nell’archivio delle caselle «Mostra originale» c’è anche nei messaggi aperti: le intestazioni si vedono '
          + 'sempre, il sorgente solo se il testo non è nell’archivio a lungo termine (dopo un risveglio della casella o l’estrazione).',
      ],
      nota: 'Il gestionale non conserva il messaggio intero ma le sue parti, e da queste ricompone il .eml e il '
        + 'sorgente; la finestra lo dice. Le intestazioni originali ci sono per i messaggi arrivati dalla versione '
        + '1.35.0; per quelli di prima, e per quelli spediti dal gestionale, sono ricostruite. Gli allegati non ancora '
        + 'controllati dall’antivirus non entrano nel .eml inoltrato né fra gli allegati dell’inoltro: il messaggio nuovo '
        + 'li elenca. Gli allegati inoltrati sono copie: all’invio ripassano dall’antivirus. Ogni originale aperto e ogni '
        + 'inoltro preparato restano nel registro eventi, come le letture.',
    },
    {
      titolo: 'Da dove viene un messaggio',
      permesso: ['posta.usa', 'utente.gestisci'],
      testo: [
        'Il mittente scritto in un’email si può falsificare. Sopra ogni messaggio ricevuto una fascia dice da dove viene '
          + 'davvero, secondo i controlli del server di ricezione di CIDIS.',
      ],
      passi: [
        'Verde «Da CIDIS, verificato»: un collega dal gestionale, o un messaggio partito dai server di posta di CIDIS.',
        'Grigia «Comunicazione automatica del gestionale»: inviti, codici e avvisi del gestionale. Non rispondere.',
        'Gialla «Esterno a CIDIS»: viene da fuori. Attenzione a collegamenti, allegati e richieste di pagamento o di '
          + 'password. Con «mittente non verificato» il dominio del mittente non conferma di averlo spedito.',
        'Rossa «Si presenta come CIDIS ma non viene dai server di CIDIS»: possibile truffa. Non rispondere, non aprire '
          + 'collegamenti o allegati, non fare pagamenti, e avvisa la Direzione.',
        'Grigia «Provenienza non verificata»: posta arrivata prima di questi controlli, o letta da un altro server.',
        'Nella conversazione ogni messaggio, anche chiuso, ha accanto al mittente un’etichetta: «CIDIS» (o «CIDIS · '
          + 'automatica»), «esterno», «esterno, non verificato», «falso CIDIS?» in rosso. I messaggi che hai spedito tu '
          + 'dalla casella sono «CIDIS».',
        'Per la posta dello storico, arrivata prima dei controlli, l’etichetta guarda solo il dominio dell’indirizzo: '
          + 'passandoci sopra leggi «Messaggio dello storico: provenienza non verificata dal server».',
        'Nell’elenco le conversazioni esterne hanno l’etichetta «esterno», quelle sospette «falso CIDIS?» in rosso. '
          + 'Le stesse etichette ci sono nell’archivio delle caselle.',
      ],
    },
    {
      titolo: 'Aprire i collegamenti di un messaggio',
      permesso: ['posta.usa', 'utente.gestisci', 'posta.archivio'],
      testo: [
        'I collegamenti si toccano nel testo del messaggio, come in ogni programma di posta, ma non si aprono '
          + 'subito: un clic per distrazione non deve portarti su un sito falso. Prima compare la finestra '
          + '**«Aprire il collegamento?»** con il sito vero a cui porta.',
      ],
      passi: [
        'Tocca il collegamento nel testo: si apre la finestra «Aprire il collegamento?».',
        'Guarda il **dominio**, scritto in grande: è la parte del nome che conta. Chiediti se è quello che ti aspetti '
          + 'da chi ti scrive. Un indirizzo come cidisonlus.org.esempio.com porta a esempio.com, non a CIDIS.',
        'Leggi gli avvisi, se ci sono, e sotto l’«Indirizzo completo».',
        'Se non sei sicuro premi «Annulla». Altrimenti premi «Apri il collegamento»: il sito si apre in una scheda '
          + 'nuova.',
      ],
      nota: 'Se non sei sicuro del sito non inserire password o dati di pagamento. Lo stesso riquadro c’è nei '
        + 'messaggi dell’archivio delle caselle.',
    },
    {
      titolo: 'La firma delle tue email',
      permesso: 'posta.usa',
      testo: [
        'Ogni email che scrivi dal gestionale parte con la firma di CIDIS: il tuo nome, la denominazione, la sede '
          + 'legale, la tua sede principale con l’indirizzo, cellulare e telefono d’ufficio, la tua email, la nota '
          + 'sull’ambiente e i due avvisi di riservatezza, in italiano e in inglese. Le righe senza dato non ci sono.',
        'La firma è già nel testo quando apri il messaggio: in fondo al messaggio nuovo, sopra il messaggio citato '
          + 'nella risposta. Se cambi «Da», diventa quella della casella scelta. Sotto la firma, all’invio, il '
          + 'gestionale aggiunge l’avviso sull’archiviazione della posta.',
        'Nelle caselle di gruppo e di progetto il messaggio porta la tua firma, oppure quella della casella se chi la '
          + 'gestisce ne ha scritta una. Spunta «Senza il mio nome» per togliere il tuo nome dalla firma: il '
          + 'messaggio resta firmato dalla casella.',
        'Se modifichi la firma a mano, quando poi cambi «Da» o spunti «Senza il mio nome», il gestionale non la '
          + 'tocca più: correggila tu.',
      ],
      passi: [
        'Dal menu, «Il mio profilo»: scrivi «Cellulare» e «Telefono d’ufficio» (cifre, spazi e i segni + / -) e premi '
          + '«Salva i numeri». Sotto vedi l’anteprima della firma.',
        'Nome, email e sede principale li cambia chi gestisce gli utenti; l’indirizzo della sede, chi gestisce le sedi.',
      ],
      nota: 'Nella firma ogni paragrafo sta su una riga: il programma di posta di chi riceve lo manda a capo da sé.',
    },
    {
      titolo: 'Allegare i file',
      permesso: 'posta.usa',
      testo: [
        'A un’email si allega qualsiasi file: PDF, fatture elettroniche (.xml e .p7m), archivi .zip e .7z, '
          + 'presentazioni, foto, audio e video, anche i file senza un tipo riconosciuto. Non si allegano i '
          + '**programmi** (.exe, .bat, .js, .msi, .apk, .iso e simili), nemmeno con un doppio nome come «fattura.pdf.exe».',
      ],
      passi: [
        'Premi «Allega un file» e scegli uno o più file, oppure trascinali sulla finestra del messaggio: compare '
          + '«Rilascia i file per allegarli».',
        'Mentre un file si carica, accanto al suo nome leggi «caricamento…», e sopra «Invia» «Caricamento di … in '
          + 'corso…»: «Invia» aspetta che finisca.',
        'Un file che non si può allegare lo dice la riga sotto «Allega un file», per esempio «“setup.exe” non si può '
          + 'allegare: i programmi non si spediscono per posta». L’avviso resta finché non lo chiudi con la ×.',
        'Se un file non arriva per un problema di rete, accanto al nome leggi «non caricato»: «Riprova» lo ricarica, '
          + 'la × lo toglie. Se premi «Invia» prima, il gestionale chiede «Spedire senza?».',
      ],
      nota: 'Per mandare un programma a un collega usa una cartella condivisa, non la posta. Un archivio .zip protetto '
        + 'da password si allega, ma il gestionale ti avvisa: l’antivirus non lo può aprire, e alcuni server di chi '
        + 'riceve lo rifiutano. L’antivirus controlla gli allegati all’invio e all’arrivo.',
    },
    {
      titolo: 'Allegati infetti, in quarantena',
      permesso: 'posta.usa',
      testo: [
        'Un allegato che l’antivirus dichiara **infetto** esce dalla posta e va in quarantena, in un archivio solo per '
          + 'questi file. Nel messaggio resta il nome, con la scritta «Infetto, in quarantena: non si scarica», senza il '
          + 'comando per scaricarlo.',
        'Non si scarica in nessun modo: non con «Scarica .eml», non con «Inoltra» o «Inoltra come allegato», non '
          + 'dall’archivio delle caselle. Il .eml e l’inoltro dicono che è rimasto fuori.',
        'Se il file è stato eliminato dalla quarantena, accanto al nome leggi «eliminato dalla quarantena il … da …».',
      ],
      nota: 'Se ti serviva quel file, chiedi a chi l’ha mandato di rispedirlo pulito, sentendolo per telefono: un '
        + 'allegato infetto vuol dire spesso che la casella di chi scrive è stata violata. La quarantena la gestiscono '
        + 'la Direzione e la Referente privacy.',
    },
    {
      titolo: 'Allegati grandi',
      permesso: 'posta.usa',
      testo: [
        'Gli allegati troppo grandi per i server di posta non partono allegati: il gestionale li mette in un '
          + '**collegamento**, e nel messaggio, subito prima della firma, compare la tabella «Allegati» con nome, '
          + 'dimensione e «Scarica». I file piccoli restano allegati veri. Prima di premere «Invia» il compositore '
          + 'ti dice quali file partiranno come collegamento.',
        'Puoi allegare file fino a 200 MB. Chi ha il collegamento scarica il file senza entrare nel gestionale, '
          + 'finché il messaggio resta nell’archivio o finché non lo revochi: per dati particolari, come salute e '
          + 'documenti d’identità, meglio non usarlo.',
      ],
      passi: [
        'Apri il messaggio che hai spedito: accanto al file leggi «collegamento» e quante volte l’hanno scaricato.',
        '«Scaricamenti» mostra quando e da quale rete; «Revoca il collegamento» lo spegne subito: chi lo apre '
          + 'legge «File non disponibile».',
      ],
      nota: 'La Referente privacy vede l’elenco di tutti i collegamenti e dei loro scaricamenti.',
    },
    {
      titolo: 'Email a scadenza',
      permesso: 'posta.usa',
      testo: [
        'Nel messaggio, «Scadenza» offre due scelte. **Scade il messaggio**: a chi sta fuori da CIDIS arriva solo un '
          + 'avviso con il collegamento a una pagina, che mostra il messaggio e gli allegati fino alla data scelta; ai '
          + 'colleghi arriva normale. **Scadono solo gli allegati**: il testo arriva normale, gli allegati partono '
          + 'tutti come collegamento e dopo la data non si scaricano più.',
        'Nel gestionale il messaggio resta com’è, archiviato come gli altri.',
      ],
      passi: [
        'Apri «Scadenza» sotto il testo e scegli il modo.',
        'In «Scade il» scrivi data e ora, oppure premi «1 giorno», «7 giorni» o «30 giorni».',
        'Per il messaggio a scadenza puoi scrivere una «Password»: comunicala a voce, non per email. Cinque '
          + 'tentativi sbagliati bloccano la pagina per un quarto d’ora.',
        'L’avviso dice «Messaggio riservato da …»; spunta «Mostra l’oggetto nell’avviso» solo se l’oggetto non dice '
          + 'niente di delicato.',
        'Nel messaggio spedito leggi «a scadenza fino al …». «Scadenza e registro» mostra chi ha aperto la pagina '
          + '(data e rete) e le password sbagliate; «Cambia la scadenza» la sposta, «Revoca ora» la chiude subito.',
      ],
      nota: 'Dopo la scadenza la pagina dice «Messaggio scaduto» con il tuo indirizzo: chi ne ha ancora bisogno ti '
        + 'scrive. Per rispondere al messaggio il destinatario scrive alla casella da cui è partito.',
    },
    {
      titolo: 'Le caselle di gruppo',
      permesso: 'posta.usa',
      testo: [
        'Nelle caselle di gruppo letto e non letto valgono per tutti: se una collega apre una conversazione, '
          + 'risulta letta anche per te. Sotto la conversazione leggi «aperto da Nome Cognome», con l’ora.',
        'Sui messaggi partiti dalla casella leggi «inviato da Nome Cognome» accanto al mittente, e nella cartella '
          + '«Inviata» sotto ogni conversazione: anche quelli mandati da un programma di posta con la credenziale '
          + 'personale. Lo vedi solo nel gestionale: chi riceve vede l’indirizzo della casella.',
        'Su una casella condivisa puoi avere «legge e scrive» oppure «legge». Con «legge», accanto alla casella '
          + 'leggi «sola lettura»: apri le conversazioni ma non rispondi, non archivi e non le segni lette, e la '
          + 'casella non entra in «Tutte le caselle».',
        'Per avere una casella di gruppo chiedila a chi gestisce gli utenti.',
        'Fra le caselle di CIDIS (la tua, quelle di gruppo e di progetto dei colleghi) il messaggio non esce dal '
          + 'gestionale: arriva subito nella casella del collega, cifrato con la chiave della sua casella. Se scrivi '
          + 'a un indirizzo @cidisonlus.org che non c’è più, l’invio si ferma e la pagina dice quale.',
      ],
    },
    {
      titolo: 'Scrivere un’email insieme ai colleghi',
      permesso: 'posta.usa',
      testo: [
        'Più persone possono scrivere la stessa email nello stesso momento, come in un documento condiviso: ognuno '
          + 'vede subito quello che scrivono gli altri, e sopra i campi leggi «Sta scrivendo anche: …» con il campo '
          + 'in cui scrivono. Poi la spedisce una persona sola.',
        'La bozza si salva da sola dalla prima lettera che scrivi, subito. Sta nella cartella «Bozze» della casella, '
          + 'in cima, con l’etichetta «bozza»; un clic la riapre. In «Tutte le caselle», che non ha cartelle, le bozze '
          + 'sono a sinistra sotto «Bozze in scrittura». Nelle caselle di gruppo le vedono e le aprono tutti i membri, '
          + 'e chi apre una bozza già aperta da un collega scrive insieme a lui.',
      ],
      passi: [
        'Premi «Scrivi» (o «Rispondi») e comincia a scrivere: sotto il titolo leggi «Bozza salvata alle 14:05», con '
          + 'l’ora dell’ultimo salvataggio, o «Salvataggio…» mentre parte.',
        'Per far scrivere un collega che non usa la casella premi «Invita a scrivere», cerca il nome e premilo. '
          + 'Riceve un avviso nel gestionale e sul telefono. Il suo nome compare accanto al comando: «×» revoca '
          + 'l’invito, e vale subito.',
        '«Chiudi» lascia la bozza nella cartella «Bozze», da riaprire quando vuoi. Con «Elimina la bozza» e poi '
          + '«Conferma: elimina per tutti» la togli anche ai colleghi.',
        '«Invia» spedisce la bozza com’è in quel momento, con quello che hanno scritto tutti. Chi la sta scrivendo '
          + 'legge «Inviata da Nome Cognome» e non la modifica più.',
      ],
      nota: 'Se chiudi la pagina, cambi scheda o passi a un’altra app sul telefono, le ultime modifiche partono '
        + 'subito; se qualcosa non è ancora salvato, il browser chiede se vuoi davvero uscire. Chi inviti vede e '
        + 'modifica solo quella bozza, e il messaggio a cui rispondi se è una risposta: non vede il resto della tua '
        + 'casella e non può spedire. La spedisci tu, o chi scrive dalla casella.',
    },
    {
      titolo: 'Se ti invitano a scrivere una bozza',
      permesso: 'posta.usa',
      passi: [
        'Ricevi la notifica «Ti hanno invitato a scrivere un’email»: «Apri la bozza» la apre. In «Posta», a '
          + 'sinistra, la trovi sotto «Bozze condivise con te».',
        'Scrivi nei campi come in un messaggio tuo: le modifiche si salvano da sole e arrivano subito agli altri. '
          + '«In risposta a …», sopra i campi, apre il messaggio a cui si risponde.',
        'Quando hai finito premi «Chiudi». La spedisce chi ti ha invitato, o un altro che scrive dalla casella.',
      ],
    },
    {
      titolo: 'Le caselle dei progetti',
      permesso: 'posta.usa',
      testo: [
        'Accanto alla casella leggi «progetto». La apri se sei il **referente CIDIS** del progetto, se hai l’incarico '
          + 'di **Project Manager su quel progetto**, o se il referente, il Project Manager o chi gestisce gli utenti '
          + 'ti ha dato l’accesso.',
        'Funziona come una casella di gruppo: il messaggio parte dall’indirizzo del progetto con la tua firma, o con '
          + 'quella della casella. Dalla pagina del progetto, riquadro «Posta del progetto», «Apri in Posta» la apre '
          + 'già scelta.',
        'Il referente CIDIS e i Project Manager del progetto possono scrivere la firma della casella, sotto «Firma '
          + 'della casella» nel riquadro «Posta del progetto». Dove scrivi {nome} il gestionale mette il nome di chi '
          + 'scrive.',
        'Le caselle dei progetti non inoltrano la posta alle caselle personali: si leggono qui.',
      ],
    },
    {
      titolo: 'Leggere la casella di un collega (supervisione)',
      permesso: 'posta.supervisiona',
      passi: [
        'In «Posta», a sinistra sotto le caselle, premi «Chiedi una supervisione».',
        'Scegli la «Casella», scrivi la «Motivazione» (almeno 20 caratteri: la leggono la Referente privacy e il '
          + 'titolare) e la «Durata in giorni». Premi «Invia la richiesta».',
        'La richiesta compare sotto «Richieste di supervisione», in attesa della Referente privacy, che decide nella '
          + 'pagina «Approvazioni». Va decisa e aperta entro sette giorni.',
        'Approvata, premi «Apri», controlla e premi «Apri la supervisione». La casella compare fra le tue con '
          + 'l’etichetta «supervisione», fino alla scadenza.',
      ],
      nota: 'Si legge soltanto: non si scrive, non si archivia, non si collega alle schede. Il titolare riceve un '
        + 'avviso all’apertura e a ogni conversazione che apri, e vede la motivazione. Per smettere prima premi '
        + '«Revoca» sotto «Supervisioni in corso».',
    },
    {
      titolo: 'Concedere e revocare una supervisione',
      permesso: 'posta.supervisione_concedi',
      passi: [
        'La richiesta arriva nella pagina «Approvazioni», con la casella, il titolare, la durata e la motivazione. '
          + 'La decide la Referente privacy (o il DPO, se nominato).',
        'Premi «Approva», oppure «Respingi» e scrivi il motivo: chi l’ha chiesta lo legge in «Posta».',
        'In «Posta», sotto «Supervisioni in corso su tutte le caselle», vedi chi legge quale casella, perché, fino '
          + 'a quando e chi l’ha concessa.',
        'Per interromperne una premi «Revoca» e conferma. Il titolare e chi supervisiona ricevono un avviso.',
      ],
      nota: 'La supervisione sulla tua casella non la revochi tu: ne sei il titolare.',
    },
    {
      titolo: 'Cambiare l’avviso in calce ai messaggi',
      permesso: 'impostazione.modifica',
      passi: [
        'In «Posta», linguetta «Casella dell’ente», riquadro «Avviso in calce ai messaggi», leggi il «Testo in '
          + 'vigore».',
        'Scrivi il «Testo nuovo» (vuoto per togliere l’avviso) e la «Motivazione». Premi «Chiedi l’approvazione».',
        'La decide la Referente privacy nella pagina «Approvazioni». Fino ad allora resta in vigore il testo di '
          + 'prima; il riquadro dice se la richiesta è in attesa, approvata o respinta.',
      ],
      nota: 'L’avviso va solo sulle email scritte dalle persone, non su quelle automatiche del gestionale. Una '
        + 'richiesta alla volta.',
    },
    {
      titolo: 'Se la tua casella è sotto supervisione',
      permesso: 'posta.usa',
      testo: [
        'Ricevi una notifica quando la supervisione si apre, a ogni conversazione che il supervisore apre e quando '
          + 'finisce. In «Posta», sotto «Supervisioni in corso», vedi chi legge la tua casella, perché e fino a '
          + 'quando.',
      ],
    },
    {
      titolo: 'Conversazioni in cancellazione',
      permesso: ['posta.usa', 'impostazione.modifica'],
      testo: [
        'La posta che entra nel gestionale resta, senza limiti di spazio. Se CIDIS accende la **regola di '
          + 'cancellazione**, le conversazioni il cui ultimo messaggio è più vecchio del termine si cancellano, '
          + 'con gli allegati. Prima restano nella linguetta «In cancellazione» almeno per i giorni di preavviso, '
          + 'con la data prevista, e ricevi una notifica per ogni tua casella in cui ne entrano.',
        'In cima a «In cancellazione» leggi la regola in vigore. Le conversazioni collegate a una scheda di '
          + 'solito restano fuori.',
        'Con la regola accesa entrano anche le conversazioni nel cestino da 30 giorni, con l’etichetta «dal '
          + 'cestino»; «Ripristina» le toglie, come «Metti in conservazione».',
      ],
      passi: [
        'Apri «Posta», linguetta «In cancellazione»: ci sono le conversazioni della tua casella e delle caselle '
          + 'di gruppo che usi, dalla data più vicina.',
        'Per tenerne una premi «Metti in conservazione», scrivi «Perché si tiene» (breve, senza dati personali) '
          + 'e conferma. La regola non la cancella più.',
        'Nella linguetta «In conservazione» vedi chi l’ha messa, quando e perché. «Togli dalla conservazione» '
          + 'la riporta sotto la regola, con un preavviso nuovo e pieno.',
      ],
      nota: 'Un messaggio nuovo nella conversazione la toglie da «In cancellazione»: il termine riparte '
        + 'dall’ultimo messaggio.',
    },
    {
      titolo: 'Cambiare la regola di cancellazione',
      permesso: 'impostazione.modifica',
      passi: [
        'In «Posta», linguetta «In cancellazione», premi «Cambia la regola».',
        'Spunta o togli «Regola accesa». Scrivi oltre quanti giorni dall’ultimo messaggio una conversazione si '
          + 'cancella (fra 90 e 3650) e i giorni di preavviso (fra 14 e 90), e scegli se la regola comprende le '
          + 'conversazioni collegate a una scheda.',
        'Scrivi la «Motivazione» (almeno 20 caratteri) e premi «Chiedi l’approvazione». La decide la Referente '
          + 'privacy (o il DPO) nella pagina «Approvazioni»; la regola nuova vale da quando è approvata.',
        'Finché la richiesta non è decisa puoi premere «Ritira la richiesta».',
      ],
      nota: 'All’accensione nessuna conversazione si cancella subito: quelle già oltre il termine si cancellano '
        + 'dopo il preavviso. Una data già annunciata non si anticipa.',
    },
    {
      titolo: 'Creare le caselle e dare l’accesso',
      permesso: 'utente.gestisci',
      passi: [
        'In «Posta», linguetta «Caselle condivise», premi «Nuova casella di gruppo». «Modalità»: «Ospitata nel '
          + 'gestionale» (senza server né password) oppure «Su un server di posta» (con indirizzo e password). Scrivi '
          + 'nome e indirizzo, spunta «Casella attiva» e premi «Salva».',
        'Per dare la casella a una persona: «Amministrazione» → «Utenti» → «Modifica» sulla persona, voce '
          + '«Caselle di gruppo», scegli la casella e il «Livello» e premi «Aggiungi». Vale subito.',
        'Per toglierla premi «Togli». Per spegnere una casella togli la spunta «Casella attiva».',
      ],
    },
    {
      titolo: 'Associare le caselle arrivate dal vecchio server',
      permesso: 'utente.gestisci',
      testo: [
        'Le caselle senza nessuno sono «da associare»: in cima alla pagina leggi «Ci sono N caselle di posta da '
          + 'associare a persone o progetti». «Associale» apre «Caselle condivise» con le sole caselle da associare.',
      ],
      passi: [
        'Sulla casella premi «Associa» e scegli di chi è.',
        '«Associa a un progetto»: cerca il progetto e conferma. La casella diventa del progetto con il suo indirizzo '
          + 'e la sua posta; la aprono da subito il referente CIDIS e i Project Manager del progetto.',
        '«Associa a persone»: aggiungi le persone con il livello, «legge» oppure «legge e scrive».',
        '«È la casella personale di…»: il gestionale propone chi entra nel gestionale con quell’indirizzo. Da quel '
          + 'momento la casella è solo sua.',
        'Una casella di progetto che non è ancora nel gestionale si collega da «Caselle di progetto» → «Collega una '
          + 'casella esistente»: scegli il progetto e scrivi l’indirizzo intero, che resta quello di oggi.',
        'Una casella di gruppo che non serve più: «Disattiva e archivia». In un passo la casella si disattiva, chi le '
          + 'scrive riceve un rifiuto in italiano e in inglese con l’indirizzo generale di CIDIS, e la casella passa '
          + 'nell’archivio delle caselle (nell’archivio a lungo termine dopo 10 giorni, salvo estensione o blocco). Il motivo proposto è '
          + '«L’indirizzo non è più in uso»; si può cambiare.',
      ],
      nota: 'Gli inoltri del vecchio server non si ricreano: per esempio un inoltro verso una casella privata '
        + 'esterna. Le caselle dei progetti si aprono dal gestionale. Ogni associazione resta nel registro.',
    },
    {
      titolo: 'Caselle ospitate e ricezione',
      permesso: ['utente.gestisci', 'impostazione.modifica'],
      passi: [
        'In «Posta», linguetta «Caselle e ricezione»: tutte le caselle, con la modalità («ospitata» o «IMAP»), '
          + 'l’ultimo messaggio ricevuto e lo stato della ricezione.',
        'Per dare la casella ospitata a più persone insieme premi «Caselle personali ospitate», controlla il '
          + '«Dominio», spunta le persone e premi «Crea». Ognuna ha l’indirizzo con cui entra nel gestionale.',
        'Per portare nel gestionale una casella che oggi sta sul server premi «Converti in ospitata» e poi '
          + '«Converti». Conversazioni, cartelle e letto/non letto restano; la lettura dal server si ferma.',
      ],
      nota: 'Converti una casella quando la sua posta passa alla ricezione del gestionale: lo decide chi gestisce '
        + 'il sistema, che poi carica dal vecchio server i messaggi rimasti.',
    },
    {
      titolo: 'Archiviare una casella',
      permesso: 'utente.gestisci',
      passi: [
        'In «Posta», linguetta «Caselle e ricezione», sulla riga della casella premi «Archivia». Vale per le caselle '
          + 'personali, di gruppo e di progetto; non per la casella dell’ente.',
        'Scegli il motivo: «La persona non lavora più con CIDIS» oppure «L’indirizzo non è più in uso», e premi '
          + '«Archivia».',
        'Da quel momento chi scrive alla casella riceve un rifiuto con il motivo, in italiano e in inglese, e '
          + 'l’indirizzo di contatto generale di CIDIS. La casella esce dalla Posta di chi la usava e passa '
          + 'nell’«Archivio delle caselle».',
      ],
      nota: 'Disattivazione e archivio si fanno qui, non sul vecchio server. Direzione e Referente privacy ricevono un '
        + 'avviso.',
    },
    {
      titolo: 'Inoltrare la posta di una casella fuori CIDIS (deroga)',
      permesso: ['utente.gestisci', 'posta.inoltri_controlla'],
      testo: [
        'La posta di CIDIS resta nel gestionale. In deroga, una casella può inoltrare la posta che arriva da fuori '
          + 'CIDIS a un indirizzo esterno, per esempio quello personale di chi la usa. Il messaggio arriva '
          + 'com’è, con il mittente vero: «Rispondi» va a chi ha scritto. La copia nel gestionale resta sempre; la '
          + 'posta indesiderata non si inoltra.',
        'La posta interna non si inoltra: le email dei colleghi, delle caselle @esterni e quelle automatiche del '
          + 'gestionale restano nel gestionale. All’indirizzo esterno arriva solo un avviso, «Nuove email interne '
          + 'nel gestionale CIDIS», con quante sono e il collegamento per leggerle, senza mittente, oggetto o testo. '
          + 'Al più un avviso ogni 10 minuti: quelle arrivate nel frattempo si contano nel successivo.',
      ],
      passi: [
        'Chi gestisce le utenze: in «Posta», linguetta «Caselle e ricezione», sulla riga della casella premi «Inoltro '
          + 'in deroga», scrivi l’indirizzo e il motivo e conferma. La riga mostra «inoltro in deroga» e l’indirizzo.',
        'Ogni concessione e ogni revoca restano nel registro eventi e la Referente privacy riceve un avviso.',
        'L’elenco «Inoltri in deroga» (in fondo a «Caselle e ricezione»; per la Referente privacy nella pagina «Posta») '
          + 'dice casella, indirizzo, motivo, chi l’ha concesso e quando. «Revoca» ferma l’inoltro entro qualche minuto.',
        'Una casella ha al più un inoltro: per cambiare indirizzo si revoca e se ne concede un altro.',
      ],
      nota: 'Un messaggio che finge di arrivare da un indirizzo di CIDIS non si inoltra e non fa partire l’avviso.',
    },
    {
      titolo: 'Usare la posta da Thunderbird, Outlook o dal telefono',
      permesso: 'posta.usa',
      testo: [
        'Le caselle che usi con «legge e scrive» (la tua, di gruppo, di progetto) si possono usare anche da un '
          + 'programma di posta: Thunderbird, Outlook, Mail del Mac, la posta dell’iPhone o di Android. Tutto resta '
          + 'sincronizzato con il gestionale: letto e non letto, bandierina, cartelle, spostamenti, cestino, posta '
          + 'inviata. La password è **tua**, anche sulle caselle di gruppo: ciò che spedisci dal programma risulta tuo.',
        'L’accesso va chiesto: lo approvano la Direzione e l’amministratore di sistema (Honey et Molly). Dopo '
          + 'l’approvazione la casella si prepara sul server di posta di CIDIS.',
      ],
      passi: [
        'In «Posta», in alto a destra, premi «Configura su client di posta».',
        'Accanto alla casella scrivi il motivo e premi '
          + '«Chiedi l’accesso da un programma di posta». Lo stato diventa «In attesa di approvazione».',
        'Approvata, lo stato è «Approvata, sincronizzazione in corso» con la percentuale: lo storico si sta copiando. '
          + 'Puoi chiudere la finestra; quando è finito ricevi un avviso.',
        'Con lo stato «Pronta» premi «Mostra la password» e poi «Copia». La password si vede **una volta sola**: '
          + 'incollala subito nel programma.',
        'Sotto ci sono le istruzioni per Thunderbird, Outlook, Apple Mail e iPhone, Android. I dati sono sempre '
          + 'questi: server mx1cidis.france4.honeymolly.net; posta in entrata IMAP, porta 993, SSL/TLS; posta in uscita '
          + 'SMTP, porta 465, SSL/TLS; nome utente l’indirizzo della casella, in entrata e in uscita.',
        'Se perdi la password o cambi computer, «Nuova password» ne crea un’altra e quella di prima smette di valere.',
        '«Revoca», con il motivo, chiude l’accesso: il programma non entra più entro qualche minuto.',
      ],
      nota: 'Le bozze scritte nel programma restano nel programma, quelle del gestionale nel gestionale. Un messaggio '
        + 'cancellato nel programma, anche svuotando il cestino, nel gestionale va nel Cestino: lo toglie la regola di '
        + 'cancellazione, come ogni altro. Le etichette non passano. Non dare a nessuno la tua password.',
    },
    {
      titolo: 'Decidere una richiesta di programma di posta',
      permesso: ['approvazione.decidi', 'approvazione.richiedi'],
      passi: [
        'La richiesta arriva in «Approvazioni» con il tipo «Accesso da un programma di posta». La motivazione dice '
          + 'la casella, la persona e il suo motivo. Chi decide riceve l’avviso «Nuova richiesta di approvazione».',
        'Servono due decisioni favorevoli, di due persone diverse: la Direzione e l’amministratore di sistema (Honey et '
          + 'Molly). Chi ha entrambi i ruoli decide una volta sola. Una contraria chiude la richiesta, e la '
          + 'persona vede «Rifiutata» con la nota.',
        'Approvata da tutte e due, la copia dello storico parte da sola; la persona riceve la password a copia finita.',
      ],
      nota: 'Lo spazio del server si allarga quando serve, a cura di Honey et Molly.',
    },
    {
      titolo: 'Programmi di posta: chi li usa e revoca',
      permesso: 'utente.gestisci',
      passi: [
        'In «Posta», linguetta «Caselle e ricezione», il riquadro «Programmi di posta» elenca chi usa una casella '
          + 'anche da un programma: casella, persona, quando è stata creata la credenziale e l’ultimo invio dal '
          + 'programma. Lo stesso elenco, per una casella sola, sta in «Caselle condivise» → «Modifica».',
        '«Revoca» e conferma: entro qualche minuto il server rifiuta la password di quella persona e chiude le sue '
          + 'connessioni aperte. Le altre persone della stessa casella continuano con le loro password.',
        'Una credenziale vale solo per chi usa la casella con «legge e scrive». Se la persona esce dalla casella, '
          + 'passa a «legge» o la sua utenza si disattiva, la credenziale si revoca da sola, con un evento nel '
          + 'registro.',
      ],
      nota: 'Le credenziali nuove le chiede la persona da «Configura su client di posta» e le approvano Direzione e '
        + 'amministratore di sistema (Honey et Molly). In «Configura su client di posta» → «Tutte le richieste» chi gestisce gli utenti vede ogni '
        + 'richiesta con il suo stato e la revoca, con il motivo.',
    },
    {
      titolo: 'L’archivio delle caselle e l’archivio a lungo termine',
      permesso: ['utente.gestisci', 'posta.archivio'],
      passi: [
        'In «Posta», linguetta «Archivio delle caselle»: le caselle archiviate, chi le ha archiviate e quando, e il conto '
          + 'alla rovescia «passa nell’archivio a lungo termine fra N giorni» (10 giorni dall’archiviazione).',
        'L’archivio a lungo termine costa poco: dopo il passaggio l’elenco dei messaggi resta visibile (mittente, '
          + 'oggetto, data), il testo e gli allegati si leggono solo dopo un recupero.',
        'Prima del passaggio puoi premere «Estendi» (altri giorni) o «Blocca il passaggio all’archivio a lungo termine» (la casella resta '
          + 'com’è finché qualcuno non preme «Sblocca»). Ogni volta scrivi il motivo.',
        '«Apri» mostra i messaggi della casella in sola lettura. Ogni apertura di un messaggio resta nel registro '
          + 'con il tuo nome.',
      ],
    },
    {
      titolo: 'Estrarre messaggi e risvegliare una casella',
      permesso: ['utente.gestisci', 'posta.archivio'],
      passi: [
        'In «Apri» spunta i messaggi da tenere, premi «Estrai», scrivi il motivo e conferma. I messaggi estratti '
          + 'restano consultabili e non vanno nell’archivio a lungo termine.',
        'Se la casella è già nell’archivio a lungo termine, l’estrazione chiede il recupero dei soli messaggi scelti. Lo segui in '
          + '«Recuperi dall’archivio a lungo termine»: data della richiesta, stato (in coda, in recupero, pronto, non riuscito), tempo '
          + 'trascorso e tempo stimato. Di solito servono 12 ore, al più 24. Quando è pronto ricevi un avviso.',
        'Nel messaggio aperto «Stampa», «Salva il messaggio in PDF» e «Scarica .eml» funzionano come nella posta. Con il testo '
          + 'nell’archivio a lungo termine il .eml si scarica dopo il risveglio o l’estrazione.',
        'Per leggere tutta una casella nell’archivio a lungo termine premi «Risveglia», scegli per quanti giorni e scrivi il motivo. A '
          + 'recupero pronto la casella si legge per intero per quei giorni: puoi estrarre messaggi o premere «Riporta '
          + 'nell’archivio caldo», che la tira fuori dall’archivio a lungo termine con un nuovo conto alla rovescia.',
      ],
      nota: 'Il recupero dall’archivio a lungo termine ha un piccolo costo per gigabyte: chiedi solo quello che serve.',
    },
    {
      titolo: 'La quarantena degli allegati infetti',
      permesso: 'posta.quarantena_gestisci',
      testo: [
        'In «Posta», linguetta «Quarantena»: gli allegati che l’antivirus ha dichiarato infetti, con casella, mittente '
          + 'e data del messaggio, nome, tipo, peso e data della quarantena. Stanno nell’archivio a lungo termine, separati dagli altri file, '
          + 'e restano finché qualcuno non li elimina: non c’è cancellazione automatica.',
        'La vedono e la usano solo la Direzione e la Referente privacy, e non si apre impersonando un altro utente. Ogni '
          + 'recupero, scaricamento ed eliminazione resta nel registro eventi; dei recuperi e degli scaricamenti la '
          + 'Referente privacy riceve un avviso.',
      ],
      passi: [
        '«Scarica in formato sicuro» serve solo a chi deve analizzare il file o a un’autorità. Leggi gli avvisi, scrivi '
          + 'il motivo, spunta «Ho letto gli avvisi» e premi «Chiedi il recupero».',
        'Il file torna dall’archivio a lungo termine di solito entro 12 ore, al più entro 24: la riga dice a che punto è e ricevi un avviso '
          + 'quando è pronto.',
        'Arriva come **zip cifrato** con password (AES-256): dentro c’è il file, con «.infetto» in fondo al nome perché '
          + 'non si apra con un doppio clic, e un LEGGIMI-PERICOLO.txt con l’impronta SHA-256. Premi «Scarica lo zip», '
          + 'spunta «Ho capito» e scaricalo: lo scarichi solo tu, per 24 ore.',
        'La **password** compare una volta sola, al primo scaricamento: annotala, o premi «Copia la password», prima di '
          + 'chiudere. Comunicala a chi riceve lo zip per un’altra strada, non nella stessa email.',
        '«Elimina definitivamente» cancella il file dalla quarantena: per confermare scrivi il nome del file. I recuperi '
          + 'in corso si annullano; nei messaggi resta il nome con chi l’ha eliminato e quando.',
      ],
      nota: 'Lo zip contiene un programma dannoso: non aprirlo e non estrarlo sul computer di lavoro. Esplora file di '
        + 'Windows e Archivio Utility del Mac non aprono gli zip cifrati AES-256: serve un programma come 7-Zip, su un '
        + 'computer isolato.',
    },
  ],
  avvertenze: [
    {
      quando: '«Infetto, in quarantena: non si scarica» accanto a un allegato.',
      cosaFare: 'L’antivirus ha trovato un programma dannoso: il file non si apre da nessuna parte. Chiedi a chi l’ha '
        + 'mandato di rispedirlo pulito, sentendolo per telefono. Se ti serve per una denuncia, chiedi alla Referente '
        + 'privacy o alla Direzione.',
    },
    {
      quando: '«La webmail non è attiva»',
      cosaFare: 'La posta dentro il gestionale non è ancora accesa per tutti. Usa la tua posta di sempre.',
    },
    {
      quando: 'Notifica «Posta in arrivo ferma: la webmail non è attiva».',
      cosaFare: 'Le caselle ospitate ricevono posta, ma il gestionale non la salva finché la webmail è spenta: i '
        + 'messaggi aspettano e arrivano all’accensione. Decide l’accensione chi gestisce le funzioni.',
    },
    {
      quando: '«La conferma non è partita (…): riprova fra poco.»',
      cosaFare: 'Il server di posta non ha accettato la conferma di lettura: l’avviso resta e la puoi rimandare più '
        + 'tardi. Se si ripete con lo stesso motivo, avvisa chi gestisce il sistema.',
    },
    {
      quando: '«C’è già un gruppo con questo nome.»',
      cosaFare: 'Nella stessa casella due gruppi non possono chiamarsi uguale, nemmeno con maiuscole diverse: scegli un '
        + 'altro nome, o apri il gruppo che c’è e cambia i suoi contatti.',
    },
    {
      quando: '«Da una casella ospitata si scrive al massimo a 100 destinatari per messaggio».',
      cosaFare: 'Il limite conta a, cc e ccn insieme: dividi l’invio in più messaggi.',
    },
    {
      quando: '«Il messaggio supera i 25 MB, allegati compresi».',
      cosaFare: 'Togli o riduci gli allegati, oppure mandali in più messaggi. Molti server di chi riceve rifiutano i '
        + 'messaggi più grandi.',
    },
    {
      quando: '«Limite di invio superato per questa casella»',
      cosaFare: 'La casella ha scritto a molti destinatari in poco tempo e il server di posta la ferma per un po’, per '
        + 'proteggerla da un uso fraudolento. Riprova più tardi. Se non hai scritto tu tutti quei messaggi, avvisa '
        + 'subito chi gestisce il sistema.',
    },
    {
      quando: 'Aprendo un collegamento, in rosso: «Il testo del collegamento mostra …, ma il collegamento porta a …».',
      cosaFare: 'Il collegamento fa vedere un sito e porta a un altro: è il trucco più usato nelle truffe via email. '
        + 'Premi «Annulla». Se il messaggio sembra di un collega, di una banca o di un ente, chiedi conferma per '
        + 'telefono; se pensi a una truffa avvisa chi gestisce il sistema.',
    },
    {
      quando: 'Aprendo un collegamento, un avviso: indirizzo numerico, nome che comincia con quello di un altro sito, '
        + 'lettere di altri alfabeti, indirizzo accorciato, reindirizzamento, sito senza https.',
      cosaFare: 'Non è per forza una truffa, ma guarda il dominio con più attenzione. Per un reindirizzamento la '
        + 'finestra mostra anche dove porta alla fine.',
    },
    {
      quando: '«Il testo è nell’archivio a lungo termine» o «L’allegato è nell’archivio a lungo termine»',
      cosaFare: 'La casella archiviata è passata nell’archivio a lungo termine: estrai il messaggio o risveglia la casella (Archivio delle '
        + 'caselle), poi riaprilo quando il recupero è pronto.',
    },
    {
      quando: 'In «Recuperi dall’archivio a lungo termine»: «oltre il tempo massimo previsto».',
      cosaFare: 'Il recupero dura più di 24 ore: avvisa chi gestisce il sistema.',
    },
    {
      quando: 'Non trovi la voce «Posta».',
      cosaFare: 'Mediatrici e mediatori non hanno la posta del gestionale.',
    },
    {
      quando: 'Una casella di gruppo è sparita dalla tua posta.',
      cosaFare: 'È stata disattivata o ti è stata tolta: chiedi a chi gestisce gli utenti.',
    },
    {
      quando: 'La casella di un progetto è sparita dalla tua posta.',
      cosaFare: 'Non sei più referente o Project Manager del progetto, ti è stata tolta o è stata disattivata: chiedi '
        + 'al referente del progetto o a chi gestisce gli utenti.',
    },
    {
      quando: '«Bandierine ed etichette per tutti le gestisce chi ha l’accesso pieno alla casella: puoi usare quelle '
        + 'solo per te.»',
      cosaFare: 'Hai la sola lettura, la casella è dell’ente o la leggi in supervisione. Usa «Solo per te»; se serve '
        + 'quella per tutti, chiedila a chi scrive dalla casella.',
    },
    {
      quando: '«Cartelle personali e spostamenti ci sono sulle caselle ospitate.»',
      cosaFare: 'La casella si legge da un altro server di posta (IMAP): le cartelle si gestiscono lì finché chi '
        + 'gestisce le caselle non la porta nel gestionale.',
    },
    {
      quando: '«C’è già una cartella con questo nome.»',
      cosaFare: 'Maiuscole e spazi non contano: «Pratiche 2026» e «pratiche2026» sono lo stesso nome, e due cartelle '
        + 'allo stesso livello non lo possono avere. Scegline un altro.',
    },
    {
      quando: 'Sotto la barra delle scelte: «… conversazioni non sono state cambiate».',
      cosaFare: 'Erano di caselle in cui hai la sola lettura, o l’azione chiede l’accesso pieno. Le altre sono state '
        + 'cambiate.',
    },
    {
      quando: '«C’è già un’etichetta con questo nome.»',
      cosaFare: 'Maiuscole e spazi non contano: «Da fare» e «da fare» sono la stessa. Usa quella che c’è, o scegli un '
        + 'altro nome.',
    },
    {
      quando: '«Su questa casella hai la sola lettura.»',
      cosaFare: 'Puoi leggere ma non rispondere né archiviare. Se devi rispondere, chiedi «legge e scrive» a chi '
        + 'decide gli accessi della casella.',
    },
    {
      quando: '«La casella personale ha l’indirizzo con cui il titolare entra nel gestionale, e questa ne ha un altro».',
      cosaFare: 'Correggi prima l’email dell’utenza in «Amministrazione» → «Utenti», oppure associa la casella a persone.',
    },
    {
      quando: 'Sotto la casella, in rosso: «lettura non riuscita alle …».',
      cosaFare: 'Il server di posta non ha risposto o non ha accettato la password. Nella «Casella personale» '
        + 'controlla la password e premi «Prova l’invio»; per una casella di gruppo avvisa chi gestisce gli utenti.',
    },
    {
      quando: '«Nessun Referente privacy e nessun DPO designato: la supervisione la concede il Referente privacy (o il DPO).»',
      cosaFare: 'Senza l’incarico di Referente privacy (o di DPO) nessuno può decidere la richiesta. Chiedi a chi '
        + 'gestisce gli utenti.',
    },
    {
      quando: '«C’è già una richiesta aperta per questa casella.»',
      cosaFare: 'Aspetta la decisione della Referente privacy, o apri la supervisione se è già approvata.',
    },
    {
      quando: '«C’è già una richiesta in attesa di decisione per l’avviso in calce.»',
      cosaFare: 'Aspetta che la Referente privacy decida quella aperta, poi chiedi di nuovo.',
    },
    {
      quando: '«C’è già una richiesta di cambio in attesa di decisione»',
      cosaFare: 'Una richiesta di cambio della regola alla volta: aspetta la Referente privacy, oppure ritira la tua.',
    },
    {
      quando: '«Non inviato: …» nella finestra per scrivere, o «Non inviato» in rosso nell’elenco.',
      cosaFare: 'Il server di posta non ha accettato il messaggio, che non è partito: sta nelle «Bozze» della casella, '
        + 'una volta sola. Riprova fra qualche minuto con «Invia», o dalla conversazione con «Riprova». Se il motivo '
        + 'è un codice che comincia per 5 (un rifiuto), controlla i destinatari con «Modifica». Se si ripete, avvisa '
        + 'chi gestisce la posta.',
    },
    {
      quando: '«Casella spenta: … non spedisce.»',
      cosaFare: 'Quella casella non è più in uso: si consulta soltanto. Scrivi da un’altra casella, scegliendola nel '
        + 'campo «Da»; una bozza già scritta la riapri e cambi «Da».',
    },
    {
      quando: '«Questa bozza non c’è più, oppure non puoi più aprirla»',
      cosaFare: 'La bozza è stata eliminata o spedita, oppure l’invito è stato revocato. Se ti serve ancora, chiedi a '
        + 'chi ti aveva invitato.',
    },
    {
      quando: '«Modifiche non ancora salvate: riprovo fra poco.»',
      cosaFare: 'La connessione si è interrotta. Non chiudere la pagina: appena torna le modifiche partono da sole.',
    },
    {
      quando: '«La bozza è stata inviata da …» mentre scrivi.',
      cosaFare: 'Un collega l’ha già spedita: quello che hai scritto dopo non è partito. Se serve, scrivi un messaggio '
        + 'nuovo.',
    },
    {
      quando: '«Risposta di assenza e regole le imposta chi ha l’accesso pieno alla casella.»',
      cosaFare: 'Sulla casella hai la sola lettura. Chiedi «legge e scrive» a chi decide gli accessi della casella.',
    },
    {
      quando: '«Il giorno di fine è già passato: la risposta non partirebbe mai.»',
      cosaFare: 'Correggi «Al giorno», oppure cancellalo per rispondere finché non la spegni.',
    },
    {
      quando: 'In «Regole»: «La cartella di questa regola non c’è più: la regola è spenta.»',
      cosaFare: 'Premi «Modifica», scegli un’altra cartella in «Sposta nella cartella» e salva: la regola si riaccende.',
    },
    {
      quando: 'In «Regole»: «L’etichetta di questa regola non c’è più: la regola è spenta.»',
      cosaFare: 'L’etichetta per tutti è stata eliminata. Premi «Modifica», scegline un’altra in «Metti l’etichetta» (o '
        + 'togli la spunta) e salva: la regola si riaccende.',
    },
    {
      quando: 'In «Metti l’etichetta»: «La casella non ha etichette per tutti».',
      cosaFare: 'Apri una conversazione della casella, menu «Bandierina ed etichette», e crea un’etichetta «per tutti»; '
        + 'poi torna alla regola.',
    },
    {
      quando: 'La risposta di assenza non è partita a qualcuno.',
      cosaFare: 'Guarda «Ultime risposte»: a chi aveva già avuto una risposta negli ultimi 7 giorni non si risponde '
        + 'di nuovo, e nemmeno a liste, messaggi automatici, posta indesiderata e mittenti non verificati. Lo stato '
        + '«non partita» vuol dire che il server di posta l’ha rifiutata.',
    },
  ],
  vediAnche: ['messenger', 'utenti', 'approvazioni', 'progetti'],
  parole: ['esterno', 'falso cidis', 'truffa', 'phishing', 'provenienza', 'email', 'mail', 'webmail', 'casella', 'qualita', 'privacy', 'smtp', 'imap', 'gruppo', 'supervisione',
    'leggere la posta di un collega', 'avviso in calce', 'revoca', 'referente privacy', 'cancellazione',
    'conservazione', 'preavviso', 'tenere una conversazione', 'ospitata', 'ricezione', 'converti', 'cpanel',
    'bozza', 'bozze', 'scrivere insieme', 'invita a scrivere', 'bozze condivise', 'collaborare',
    'progetto', 'casella del progetto', 'sola lettura', 'da associare', 'associa', 'inoltro',
    'firma', 'telefono', 'cellulare', 'profilo', 'senza il mio nome', 'riservatezza',
    'allegato grande', 'collegamento', 'scarica', 'revoca il collegamento',
    'conferma di lettura', 'ricevuta di ritorno', 'priorità', 'urgente', 'importante', 'mostra originale',
    'intestazioni', 'sorgente', 'inoltra come allegato', 'eml', 'gruppi di contatti', 'lista di distribuzione',
    'importa contatti', 'esporta contatti', 'vcard', 'vcf', 'csv', 'roundcube',
    'scadenza', 'a scadenza', 'password', 'messaggio riservato', 'revoca ora', 'proroga',
    'link', 'collegamenti nel messaggio', 'apri il collegamento', 'phishing', 'truffa', 'dominio', 'sito falso',
    'rubrica', 'contatti', 'indirizzi', 'suggerimenti', 'selettore', 'da leggere', 'non letti', 'outlook',
    'bandierina', 'bandiera', 'contrassegno', 'colore', 'rosso', 'etichetta', 'etichette', 'categoria', 'tag',
    'assenza', 'fuori ufficio', 'ferie', 'risposta automatica', 'vacanza', 'regole', 'filtri', 'smistamento',
    'sposta in cartella', 'roundcube', 'selezione', 'seleziona tutte', 'più messaggi', 'cartella', 'cartelle',
    'sottocartella', 'sposta', 'trascina', 'ordina', 'ordine', 'vista', 'per messaggi', 'righe per pagina',
    'segna come letta', 'tastiera', 'scorciatoie', 'preferenze',
    'quarantena', 'infetto', 'virus', 'antivirus', 'programma dannoso', 'malware', 'zip cifrato', 'formato sicuro'],
}

export default capitolo
